/**
 * Notifications - Send messages to players (all/specific, platform-filtered)
 */
import React, { useEffect, useState } from "react";
import {
  sendNotification,
  listNotifications,
  deleteNotification,
  listDevices,
} from "../lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/radio-group";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Plus, Trash2, Bell, Send } from "lucide-react";
import { ADMIN } from "../lib/testIds";

const ALL_PLATFORMS = ["windows", "android", "ios", "macos", "web", "linux"];

export default function Notifications() {
  const { toast } = useToast();
  const [notifications, setNotifications] = useState([]);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const [open, setOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({
    title: "",
    message: "",
    target_type: "all",
    target_devices: [],
    target_platforms: [...ALL_PLATFORMS],
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const [n, d] = await Promise.all([listNotifications(), listDevices()]);
      setNotifications(n);
      setDevices(d);
    } catch (e) {
      toast({ title: "Failed to load", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const resetForm = () => {
    setForm({
      title: "",
      message: "",
      target_type: "all",
      target_devices: [],
      target_platforms: [...ALL_PLATFORMS],
    });
  };

  const handleSend = async () => {
    if (!form.title || !form.message) {
      toast({ title: "Title and message required", variant: "destructive" });
      return;
    }
    if (form.target_type === "specific" && form.target_devices.length === 0) {
      toast({ title: "Select at least one device", variant: "destructive" });
      return;
    }
    setSending(true);
    try {
      const result = await sendNotification(form);
      toast({
        title: "Notification sent",
        description: `Delivered to ${result.delivery_stats?.sent || 0} device(s)`,
      });
      setOpen(false);
      resetForm();
      refresh();
    } catch (e) {
      toast({
        title: "Failed to send",
        description: e.response?.data?.detail || e.message,
        variant: "destructive",
      });
    } finally {
      setSending(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this notification?")) return;
    try {
      await deleteNotification(id);
      toast({ title: "Deleted" });
      refresh();
    } catch (e) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    }
  };

  const togglePlatform = (p) => {
    setForm((prev) => ({
      ...prev,
      target_platforms: prev.target_platforms.includes(p)
        ? prev.target_platforms.filter((x) => x !== p)
        : [...prev.target_platforms, p],
    }));
  };

  const toggleDevice = (id) => {
    setForm((prev) => ({
      ...prev,
      target_devices: prev.target_devices.includes(id)
        ? prev.target_devices.filter((x) => x !== id)
        : [...prev.target_devices, id],
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <p className="text-slate-600">
          Send messages to player apps. Filter by platform or target specific devices.
        </p>
        <Button
          data-testid={ADMIN.btnSendNotification}
          onClick={() => setOpen(true)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Notification
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Loading...</div>
          ) : notifications.length === 0 ? (
            <div className="p-12 text-center text-slate-500">
              <Bell className="w-10 h-10 mx-auto mb-3 opacity-40" />
              <p>No notifications sent yet.</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Message</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Platforms</TableHead>
                  <TableHead>Stats</TableHead>
                  <TableHead>Sent</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {notifications.map((n) => (
                  <TableRow key={n.id} data-testid={ADMIN.notifRow(n.id)}>
                    <TableCell className="font-semibold">{n.title}</TableCell>
                    <TableCell className="max-w-xs truncate text-sm text-slate-600">
                      {n.message}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {n.target_type === "all"
                          ? "All devices"
                          : `${n.target_devices?.length || 0} specific`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs">
                      {n.target_platforms?.length === ALL_PLATFORMS.length
                        ? "All"
                        : n.target_platforms?.join(", ")}
                    </TableCell>
                    <TableCell className="text-xs">
                      <span className="text-emerald-600">
                        {n.delivery_stats?.sent || 0} sent
                      </span>{" "}
                      ·{" "}
                      <span className="text-blue-600">
                        {n.delivery_stats?.read || 0} read
                      </span>
                    </TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {new Date(n.created_at).toLocaleString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        data-testid={ADMIN.btnDeleteNotif(n.id)}
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDelete(n.id)}
                        className="text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent data-testid={ADMIN.sendNotifDialog} className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
            <DialogDescription>
              Compose a message and choose target devices or platforms.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Title *</Label>
              <Input
                data-testid={ADMIN.notifTitle}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g. New playlists added"
              />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea
                data-testid={ADMIN.notifMessage}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Your message to players..."
                rows={3}
              />
            </div>

            <div>
              <Label className="mb-2 block">Target</Label>
              <RadioGroup
                value={form.target_type}
                onValueChange={(v) => setForm({ ...form, target_type: v })}
                data-testid={ADMIN.notifTargetType}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="all" id="t-all" />
                  <Label htmlFor="t-all" className="cursor-pointer font-normal">
                    All devices (filtered by platforms below)
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem value="specific" id="t-specific" />
                  <Label htmlFor="t-specific" className="cursor-pointer font-normal">
                    Specific devices
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {form.target_type === "all" && (
              <div>
                <Label className="mb-2 block">Target Platforms</Label>
                <div className="grid grid-cols-3 gap-2">
                  {ALL_PLATFORMS.map((p) => (
                    <label
                      key={p}
                      className="flex items-center gap-2 cursor-pointer text-sm"
                    >
                      <Checkbox
                        checked={form.target_platforms.includes(p)}
                        onCheckedChange={() => togglePlatform(p)}
                      />
                      <span className="capitalize">{p}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {form.target_type === "specific" && (
              <div>
                <Label className="mb-2 block">
                  Select Devices ({form.target_devices.length} selected)
                </Label>
                <div className="max-h-48 overflow-y-auto border border-slate-200 rounded-lg p-2 space-y-1">
                  {devices.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-3">
                      No devices available
                    </p>
                  ) : (
                    devices.map((d) => (
                      <label
                        key={d.device_id}
                        className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer text-sm"
                      >
                        <Checkbox
                          checked={form.target_devices.includes(d.device_id)}
                          onCheckedChange={() => toggleDevice(d.device_id)}
                        />
                        <span className="capitalize font-medium">{d.platform}</span>
                        <span className="font-mono text-xs text-slate-500">
                          {d.device_id}
                        </span>
                        <span className="text-xs text-slate-500">
                          {d.device_model || ""}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              data-testid={ADMIN.notifSendConfirm}
              onClick={handleSend}
              disabled={sending}
              className="bg-orange-500 hover:bg-orange-600"
            >
              <Send className="w-4 h-4 mr-2" />
              {sending ? "Sending..." : "Send Notification"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
